import type { QuestionDefinition, QuestionId } from "../types/friction-finder";

export const questions: readonly QuestionDefinition[] = [
  {
    id: "primary_friction",
    reviewLabel: "Main friction",
    heading: "What is making your business harder to run than it should be?",
    instruction: "Choose the one issue that best describes the main pressure.",
    type: "single",
    min: 1,
    max: 1,
    validation: {
      empty: "Choose the main issue before continuing.",
      custom: "Describe the main friction in 10 to 160 characters.",
    },
    options: [
      { id: "manual_work", label: "Too much work depends on manual steps", snapshotPhrase: "too much work depends on manual steps" },
      { id: "fragmented_information", label: "Information is spread across too many places", snapshotPhrase: "information is spread across too many places" },
      { id: "inconsistent_work", label: "Teams are working in different ways", snapshotPhrase: "teams are working in different ways" },
      { id: "limited_visibility", label: "Leaders do not have clear visibility", snapshotPhrase: "leaders do not have clear visibility" },
      { id: "growth_slowdown", label: "Work is slowing down as the business grows", snapshotPhrase: "work is slowing down as the business grows" },
      { id: "customer_inconsistency", label: "Customers are experiencing inconsistency", snapshotPhrase: "customers are experiencing inconsistency" },
      { id: "slow_decisions", label: "Decisions take too long", snapshotPhrase: "decisions take too long" },
      { id: "other_friction", label: "Something else" },
    ],
  },
  {
    id: "friction_area",
    reviewLabel: "Where it is showing up",
    heading: "Where is this friction most noticeable?",
    instruction: "Choose the area where the issue is most visible.",
    type: "single",
    min: 1,
    max: 1,
    validation: { empty: "Choose where the friction is most noticeable." },
    options: [
      { id: "leadership_decisions", label: "Leadership and decision-making", snapshotPhrase: "leadership and decision-making" },
      { id: "sales_business_development", label: "Sales and business development", snapshotPhrase: "sales and business development" },
      { id: "client_onboarding_service", label: "Client onboarding and service", snapshotPhrase: "client onboarding and service" },
      { id: "delivery_operations", label: "Delivery and day-to-day operations", snapshotPhrase: "delivery and day-to-day operations" },
      { id: "systems_data_reporting", label: "Systems, data and reporting", snapshotPhrase: "systems, data and reporting" },
      { id: "finance_administration", label: "Finance and administration", snapshotPhrase: "finance and administration" },
      { id: "people_accountability", label: "People, roles and accountability", snapshotPhrase: "people, roles and accountability" },
      { id: "across_business", label: "Across several areas of the business", snapshotPhrase: "several areas across the business" },
    ],
  },
  {
    id: "affected_groups",
    reviewLabel: "Who it affects",
    heading: "Who feels the impact most?",
    instruction: "Choose up to three groups.",
    type: "multi",
    min: 1,
    max: 3,
    validation: { empty: "Choose at least one affected group.", over: "Choose no more than three groups." },
    options: [
      { id: "owners_directors", label: "Owners or directors", snapshotPhrase: "owners or directors" },
      { id: "leadership_team", label: "Leadership team", snapshotPhrase: "the leadership team" },
      { id: "managers_team_leaders", label: "Managers or team leaders", snapshotPhrase: "managers or team leaders" },
      { id: "frontline_delivery", label: "Frontline or delivery teams", snapshotPhrase: "frontline or delivery teams" },
      { id: "sales_client_facing", label: "Sales or client-facing teams", snapshotPhrase: "sales or client-facing teams" },
      { id: "administration_support", label: "Administration or support teams", snapshotPhrase: "administration or support teams" },
      { id: "customers_clients", label: "Customers or clients", snapshotPhrase: "customers or clients" },
      { id: "everyone", label: "Everyone across the business", snapshotPhrase: "people across the business" },
    ],
  },
  {
    id: "friction_impacts",
    reviewLabel: "What it is causing",
    heading: "What is this friction causing or preventing?",
    instruction: "Choose up to three impacts.",
    type: "multi",
    min: 1,
    max: 3,
    validation: { empty: "Choose at least one impact.", over: "Choose no more than three impacts." },
    options: [
      { id: "wasted_time", label: "Wasted time and duplicated work", snapshotPhrase: "wasted time and duplicated work" },
      { id: "slower_delivery", label: "Slower delivery or missed deadlines", snapshotPhrase: "slower delivery or missed deadlines" },
      { id: "errors_rework", label: "Errors, rework or inconsistent quality", snapshotPhrase: "errors, rework or inconsistent quality" },
      { id: "poor_visibility", label: "Poor visibility or unreliable reporting", snapshotPhrase: "poor visibility or unreliable reporting" },
      { id: "delayed_decisions", label: "Delayed decisions", snapshotPhrase: "delayed decisions" },
      { id: "team_frustration", label: "Team frustration or unclear accountability", snapshotPhrase: "team frustration or unclear accountability" },
      { id: "lost_sales", label: "Lost sales or missed opportunities", snapshotPhrase: "lost sales or missed opportunities" },
      { id: "customer_frustration", label: "Customer frustration or reduced confidence", snapshotPhrase: "customer frustration or reduced confidence" },
    ],
  },
  {
    id: "timing_intent",
    reviewLabel: "Why it matters now",
    heading: "Are you fixing today or planning for tomorrow?",
    instruction: "Choose the statement that best reflects why this matters now.",
    type: "single",
    min: 1,
    max: 1,
    validation: { empty: "Choose whether you are fixing today or planning for tomorrow." },
    options: [
      { id: "fixing_today", label: "Fixing Today", description: "It is already affecting performance and needs attention now." },
      { id: "planning_tomorrow", label: "Planning for Tomorrow", description: "Growth or change will make it a larger problem if it is left unresolved." },
    ],
  },
  {
    id: "desired_outcomes",
    reviewLabel: "What better looks like",
    heading: "What would better look like?",
    instruction: "Choose up to three outcomes.",
    type: "multi",
    min: 1,
    max: 3,
    validation: {
      empty: "Choose at least one desired outcome.",
      over: "Choose no more than three outcomes.",
      custom: "Describe the outcome in 5 to 120 characters.",
    },
    options: [
      { id: "less_manual_work", label: "Less manual work", snapshotPhrase: "less manual work" },
      { id: "clearer_accountability", label: "Clearer roles and accountability", snapshotPhrase: "clearer roles and accountability" },
      { id: "consistent_working", label: "More consistent ways of working", snapshotPhrase: "more consistent ways of working" },
      { id: "better_visibility", label: "Better visibility and reporting", snapshotPhrase: "better visibility and reporting" },
      { id: "faster_decisions", label: "Faster, more confident decisions", snapshotPhrase: "faster, more confident decisions" },
      { id: "smoother_customer_experience", label: "A smoother customer experience", snapshotPhrase: "a smoother customer experience" },
      { id: "supportive_systems", label: "Systems that better support the work", snapshotPhrase: "systems that better support the work" },
      { id: "growth_capacity", label: "More capacity to grow", snapshotPhrase: "more capacity to grow" },
      { id: "other_outcome", label: "Something else" },
    ],
  },
] as const;

export const questionById = Object.fromEntries(questions.map((question) => [question.id, question])) as Record<QuestionId, QuestionDefinition>;

export function optionFor(questionId: QuestionId, optionId: string) {
  return questionById[questionId].options.find((option) => option.id === optionId);
}
